import React from "react"
import { close } from "@app/scripts/hooks/icons"
import { useSearchspringFilters } from "@app/scripts/hooks/use-searchspring-filters"
import { sizeLabel } from "@app/scripts/lib/utils"
import { fabricLabels } from "@app/scripts/lib/fabric"

type Props = {
  // Every raw value in the Material facet, so a fibre chip can tell "wholly
  // selected" from "one shared blend happens to be active". Absent until
  // tags_x_fabric is exposed as a facet, in which case no fabric chips render.
  fabricValues?: string[]
}

export function FilterSortDrawerActiveItems({ fabricValues }: Props = {}) {
  const {
    activeFilters,
    handleToggleFilter,
    handleRemoveFilter,
    handleClearFilters,
  } = useSearchspringFilters()

  // Sanitize activeFilters to make sure they show front end friendly.
  // Size filters relabel to alpha and merge to one chip per label (#1747);
  // removing a merged chip toggles all its active keys in one update.
  const sizeGroups = new Map<string, string[]>()
  const activeFiltersArray: { key: string; label: string }[] = []

  // Material chips show the fibre, not the raw tag. Without this a single Linen
  // click produced eight chips reading "68%ten/17%nyl/15%lin" and the like. A
  // fibre only earns a chip once every one of its keys is active, so the blends
  // it shares with another fibre cannot conjure a chip on their own.
  const fabricKeysByLabel = new Map<string, string[]>()
  ;(fabricValues ?? []).forEach((rawValue) => {
    fabricLabels(rawValue).forEach((label) => {
      const keys = fabricKeysByLabel.get(label) || []
      keys.push(`tags_x_fabric:${rawValue}`)
      fabricKeysByLabel.set(label, keys)
    })
  })

  const fabricChips = Array.from(fabricKeysByLabel.entries())
    .filter(([, keys]) => keys.every((key) => activeFilters?.has(key)))
    .map(([label, keys]) => ({ key: keys.join(","), label }))

  const claimedFabricKeys = new Set(
    Array.from(fabricKeysByLabel.entries())
      .filter(([, keys]) => keys.every((key) => activeFilters?.has(key)))
      .flatMap(([, keys]) => keys)
  )

  Array.from(activeFilters ?? []).forEach((item) => {
    const [field, rawValue = ""] = item.split(":")
    let label = rawValue.toLowerCase()

    if (field === "ss_filter_sizes") {
      const sizeLabelText = sizeLabel(rawValue)
      const group = sizeGroups.get(sizeLabelText) || []
      group.push(item)
      sizeGroups.set(sizeLabelText, group)
      return
    }

    // Already represented by a fibre chip above.
    if (field === "tags_x_fabric" && claimedFabricKeys.has(item)) return

    if (field === "ss_price") {
      const [low, high] = rawValue.split("<>")
      if (low && high) {
        label = `$${low} - $${high}`
      }
    }

    activeFiltersArray.push({ key: item, label })
  })

  const sizeChips = Array.from(sizeGroups.entries()).map(([label, keys]) => ({
    key: keys.join(","),
    label,
  }))

  if (
    activeFiltersArray.length === 0 &&
    sizeChips.length === 0 &&
    fabricChips.length === 0
  ) {
    return null
  }

  return (
    <div className="filter-sort-drawer-active-items--list">
      {activeFiltersArray.map(({ key, label }) => (
        <div
          onClick={() => handleRemoveFilter(key)}
          key={key}
          className="filter-sort-drawer--active-item"
        >
          {label} <span>{close}</span>
        </div>
      ))}
      {[...sizeChips, ...fabricChips].map(({ key, label }) => (
        <div
          onClick={() => handleToggleFilter(key)}
          key={key}
          className="filter-sort-drawer--active-item"
        >
          {label} <span>{close}</span>
        </div>
      ))}
      <p
        className="filter-sort-active-items--close underline"
        onClick={handleClearFilters}
      >
        Clear All
      </p>
    </div>
  )
}
